import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Plus, Key, Trash2, Pencil, Search, X, ArrowUpDown, ArrowUp, ArrowDown,
  Users, ShieldCheck, ShieldAlert, UserMinus, UserX, Ban, Lock, Unlock, Archive, CheckCircle2, AlertTriangle, ArrowRight,
} from "lucide-react";
import { formatDateES } from "@/lib/dateUtils";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { evaluatePassword } from "@/lib/passwordPolicy";

const BADGE_COLORS = [
  "bg-red-100 text-red-700 border-red-200",
  "bg-blue-100 text-blue-700 border-blue-200",
  "bg-green-100 text-green-700 border-green-200",
  "bg-purple-100 text-purple-700 border-purple-200",
  "bg-amber-100 text-amber-700 border-amber-200",
  "bg-slate-100 text-slate-700 border-slate-200",
];

const PERSON_TYPE_LABEL = {
  empleado: "Empleado", externo: "Externo", proveedor: "Proveedor",
  tecnico_sistema: "Técnico", super_admin: "Super Admin",
};
const PERSON_TYPE_BADGE = {
  empleado: "bg-blue-50 text-blue-700 border-blue-200",
  externo: "bg-purple-50 text-purple-700 border-purple-200",
  proveedor: "bg-indigo-50 text-indigo-700 border-indigo-200",
  tecnico_sistema: "bg-slate-200 text-slate-700 border-slate-300",
  super_admin: "bg-amber-50 text-amber-700 border-amber-200",
};
const STATUS_CONFIG = {
  activo:      { label: "Activo",      badge: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle2 },
  desactivado: { label: "Desactivado", badge: "bg-slate-100 text-slate-600 border-slate-200", icon: UserMinus },
  bloqueado:   { label: "Bloqueado",   badge: "bg-red-100 text-red-700 border-red-200",       icon: Ban },
  archivado:   { label: "Archivado",   badge: "bg-amber-100 text-amber-800 border-amber-200", icon: Archive },
};

const EMPTY_CREATE = {
  mode: "existing", // existing | new | exception
  email: "", password: "", role: "empleado",
  employee_id: "",
  // ficha rapida
  ne_first_name: "", ne_last_name: "", ne_email: "", ne_phone: "",
  ne_center_id: "", ne_department_id: "", ne_position_id: "", ne_direct_manager_id: "",
  ne_person_type: "empleado", ne_company_name: "", ne_notes: "",
  // excepcion
  exception_reason: "tecnico_sistema", exception_notes: "",
};

export default function UsersPage() {
  const { axiosAuth, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = currentUser?.role === "super_admin";

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [roles, setRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [showPwChange, setShowPwChange] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(null); // user
  const [statusForm, setStatusForm] = useState({ status: "activo", reason: "" });
  const [deleteConfirm, setDeleteConfirm] = useState(null); // user
  const [deleteText, setDeleteText] = useState("");

  // Form state for create / edit
  const [form, setForm] = useState({ ...EMPTY_CREATE });
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "", role: "empleado" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });

  // Catalogs for "new employee" panel
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [employeeQuery, setEmployeeQuery] = useState("");
  const [centers, setCenters] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);

  // Orphans count
  const [orphansCount, setOrphansCount] = useState(0);

  // Filters
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLinkUser, setFilterLinkUser] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [page, setPage] = useState(1);
  const LIMIT = 50;

  const hasActiveFilters = useMemo(() =>
    search || filterRole !== "all" || filterStatus !== "all" || filterLinkUser !== "all",
    [search, filterRole, filterStatus, filterLinkUser]
  );

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (filterRole !== "all") params.set("role", filterRole);
      if (filterStatus !== "all") {
        // Keep backward compat: status=activo => active=active, otherwise filter manually post-fetch
        params.set("active", filterStatus === "activo" ? "active" : "inactive");
      }
      if (filterLinkUser !== "all") params.set("link_filter", filterLinkUser);
      params.set("sort_by", sortBy);
      params.set("sort_order", sortOrder);
      params.set("page", String(page));
      params.set("limit", String(LIMIT));

      const res = await axiosAuth().get(`/users?${params.toString()}`);
      let items = res.data.items || [];
      if (filterStatus !== "all" && filterStatus !== "activo") {
        items = items.filter(u => u.status === filterStatus);
      }
      setUsers(items);
      setTotal(res.data.total || 0);
    } catch { toast.error("Error al cargar usuarios"); }
    finally { setLoading(false); }
  }, [axiosAuth, search, filterRole, filterStatus, filterLinkUser, sortBy, sortOrder, page]);

  const fetchOrphans = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/users/orphans");
      setOrphansCount(res.data.total || 0);
    } catch { /* ignore */ }
  }, [axiosAuth]);

  const fetchRoles = useCallback(async () => {
    try {
      const [active, all] = await Promise.all([
        axiosAuth().get("/roles/active"),
        axiosAuth().get("/roles"),
      ]);
      setRoles(active.data);
      setAllRoles(all.data);
    } catch { /* ignore */ }
  }, [axiosAuth]);

  const fetchAvailableEmployees = useCallback(async (q = "") => {
    try {
      const params = q ? `?q=${encodeURIComponent(q)}` : "";
      const res = await axiosAuth().get(`/employees/available-for-link${params}`);
      setAvailableEmployees(res.data || []);
    } catch { /* ignore */ }
  }, [axiosAuth]);

  const fetchCatalogs = useCallback(async () => {
    try {
      const [c, d, p] = await Promise.all([
        axiosAuth().get("/centers"),
        axiosAuth().get("/departments"),
        axiosAuth().get("/positions"),
      ]);
      setCenters(c.data || []);
      setDepartments(d.data || []);
      setPositions(p.data || []);
    } catch { /* ignore */ }
  }, [axiosAuth]);

  useEffect(() => { fetchRoles(); fetchCatalogs(); fetchOrphans(); }, [fetchRoles, fetchCatalogs, fetchOrphans]);
  useEffect(() => { setPage(1); }, [search, filterRole, filterStatus, filterLinkUser, sortBy, sortOrder]);
  useEffect(() => { const t = setTimeout(() => fetchUsers(), 250); return () => clearTimeout(t); }, [fetchUsers]);

  // Debounced employee search in create dialog
  useEffect(() => {
    if (!showCreate || form.mode !== "existing") return;
    const t = setTimeout(() => fetchAvailableEmployees(employeeQuery), 200);
    return () => clearTimeout(t);
  }, [employeeQuery, showCreate, form.mode, fetchAvailableEmployees]);

  const getRoleBadge = (roleSlug) => {
    const idx = allRoles.findIndex(r => r.slug === roleSlug);
    return BADGE_COLORS[idx >= 0 ? idx % BADGE_COLORS.length : BADGE_COLORS.length - 1];
  };
  const getRoleLabel = (roleSlug) => allRoles.find(r => r.slug === roleSlug)?.name || roleSlug;

  const resetCreateForm = () => { setForm({ ...EMPTY_CREATE }); setEmployeeQuery(""); };

  const handleCreate = async () => {
    if (!form.email) {
      toast.error("Email obligatorio");
      return;
    }
    // Validar política de contraseñas en cliente (UX). Backend valida también.
    const evalRes = evaluatePassword(form.password, {
      userData: {
        name: form.ne_first_name + " " + form.ne_last_name,
        email: form.email,
        phone: form.ne_phone,
      },
      role: form.role,
    });
    if (!evalRes.is_valid) {
      toast.error(evalRes.reasons[0] || "La contraseña no cumple la política de seguridad");
      return;
    }
    const payload = {
      email: form.email,
      password: form.password,
      role: form.role,
      name: "",
    };
    if (form.mode === "existing") {
      if (!form.employee_id) { toast.error("Selecciona un empleado/persona existente"); return; }
      payload.employee_id = form.employee_id;
    } else if (form.mode === "new") {
      if (!form.ne_first_name && !form.ne_company_name) {
        toast.error("Indica nombre o razón social"); return;
      }
      payload.new_employee = {
        first_name: form.ne_first_name,
        last_name: form.ne_last_name,
        email: form.ne_email || form.email,
        phone: form.ne_phone,
        center_id: form.ne_center_id,
        department_id: form.ne_department_id,
        position_id: form.ne_position_id,
        direct_manager_id: form.ne_direct_manager_id,
        person_type: form.ne_person_type,
        company_name: form.ne_company_name,
        internal_notes: form.ne_notes,
      };
    } else if (form.mode === "exception") {
      if (!isSuperAdmin) { toast.error("Solo Super Admin puede crear excepciones"); return; }
      if (!form.exception_notes || form.exception_notes.length < 5) {
        toast.error("Motivo detallado obligatorio para excepciones"); return;
      }
      payload.is_system_exception = true;
      payload.exception_reason = form.exception_reason;
      payload.exception_notes = form.exception_notes;
    }
    try {
      await axiosAuth().post("/auth/register", payload);
      toast.success("Usuario creado correctamente");
      setShowCreate(false);
      resetCreateForm();
      fetchUsers();
      fetchOrphans();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al crear"); }
  };

  const handleEdit = async () => {
    if (!editUser) return;
    const data = {};
    if (editForm.email) data.email = editForm.email;
    if (editForm.role) data.role = editForm.role;
    if (editForm.password) data.password = editForm.password;
    try {
      await axiosAuth().put(`/users/${editUser.id}`, data);
      toast.success("Usuario actualizado");
      setEditUser(null);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al actualizar"); }
  };

  // Iter 231 (P2) — Sincronizar email del user con el email corporativo del empleado.
  const syncEmailWithEmployee = async (userId, opts = {}) => {
    const target = users.find((u) => u.id === userId) || editUser;
    if (!target?.employee_id) {
      toast.error("El usuario no está vinculado a ningún empleado");
      return;
    }
    if (!opts.silent) {
      const ok = window.confirm(
        "Se actualizará el email de acceso del usuario con el email corporativo del empleado vinculado. ¿Continuar?"
      );
      if (!ok) return;
    }
    try {
      const res = await axiosAuth().post(
        `/users/sync-linked-employee-emails?user_id=${encodeURIComponent(userId)}&verbose=true`
      );
      const d = res.data || {};
      const changed = (d.changes || []).length;
      const inSync = d.already_in_sync_count || 0;
      const skipped = (d.skipped || [])[0];
      if (changed > 0) {
        const c = d.changes[0];
        toast.success("Email actualizado correctamente.");
        // Reflect new email in edit modal if open
        if (editUser?.id === userId) {
          setEditForm((f) => ({ ...f, email: c.new_email }));
          setEditUser((u) => (u ? { ...u, email: c.new_email, email_sync_status: "synced" } : u));
        }
      } else if (inSync > 0) {
        toast.info("El email ya estaba sincronizado con el del empleado.");
      } else if (skipped) {
        const reasons = {
          employee_not_found: "El empleado vinculado no existe.",
          employee_email_empty: "El empleado no tiene email corporativo.",
          email_already_used_by_other_user: "Este email ya está usado por otro usuario.",
        };
        toast.error(reasons[skipped.reason] || `No se pudo sincronizar: ${skipped.reason}`);
      } else {
        toast.warning("Nada que sincronizar.");
      }
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al sincronizar");
    }
  };

  const handleChangeStatus = async () => {
    if (!showStatusDialog) return;
    try {
      await axiosAuth().put(`/users/${showStatusDialog.id}/status`, statusForm);
      toast.success(`Estado actualizado a "${statusForm.status}"`);
      setShowStatusDialog(null);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cambiar estado"); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    if (deleteText !== "ELIMINAR") { toast.error("Escribe ELIMINAR exactamente"); return; }
    try {
      await axiosAuth().delete(`/users/${deleteConfirm.id}?confirm=ELIMINAR`);
      toast.success("Usuario eliminado definitivamente");
      setDeleteConfirm(null); setDeleteText("");
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  const handlePasswordChange = async () => {
    if (pwForm.new_password !== pwForm.confirm) { toast.error("Las contraseñas no coinciden"); return; }
    const evalRes = evaluatePassword(pwForm.new_password, {
      userData: { name: currentUser?.name, email: currentUser?.email },
      role: currentUser?.role,
    });
    if (!evalRes.is_valid) { toast.error(evalRes.reasons[0] || "Contraseña insegura"); return; }
    try {
      await axiosAuth().post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Contraseña actualizada");
      setShowPwChange(false);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleSendActivationLink = async (u) => {
    if (!u?.id) return;
    if (!window.confirm(`Enviar un enlace de activación a ${u.email}? El usuario podrá definir su nueva contraseña sin que tú la veas. El enlace caduca en 48h.`)) return;
    try {
      await axiosAuth().post(`/users/${u.id}/send-activation-link`);
      toast.success(`Enlace de activación enviado a ${u.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar enlace");
    }
  };

  const handleGenerateTempPassword = async (u) => {
    if (!u?.id) return;
    if (!window.confirm(`Generar contraseña temporal para ${u.email}? El usuario deberá cambiarla en el primer acceso.`)) return;
    try {
      const res = await axiosAuth().post(`/users/${u.id}/generate-temp-password`);
      // Mostrar la contraseña temporal en un prompt (transmitir por canal seguro)
      const temp = res.data?.temp_password || "";
      // eslint-disable-next-line no-alert
      window.prompt(
        `Contraseña temporal para ${u.email}\n\nCopia y transmite por canal seguro. NO volverá a mostrarse.\nEl usuario deberá cambiarla en el primer acceso.`,
        temp,
      );
      toast.success("Contraseña temporal generada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const openEdit = (u) => { setEditUser(u); setEditForm({ name: u.name, email: u.email, password: "", role: u.role }); };
  const openStatus = (u, target) => { setShowStatusDialog(u); setStatusForm({ status: target || (u.status === "activo" ? "desactivado" : "activo"), reason: "" }); };

  const clearFilters = () => {
    setSearch(""); setFilterRole("all"); setFilterStatus("all"); setFilterLinkUser("all");
    setSortBy("name"); setSortOrder("asc");
  };
  const toggleSort = (field) => {
    if (sortBy === field) setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortOrder("asc"); }
  };
  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 text-slate-300" />;
    return sortOrder === "asc" ? <ArrowUp className="w-3 h-3 text-slate-700" /> : <ArrowDown className="w-3 h-3 text-slate-700" />;
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="space-y-4" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase" data-testid="users-title">Gestión de Usuarios</h1>
          <p className="text-sm text-slate-500 mt-0.5" data-testid="users-count">
            <span className="font-semibold text-slate-700">{total}</span> usuarios registrados
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowPwChange(true)} className="rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-3 border-slate-300" data-testid="change-password-btn">
            <Key className="w-3.5 h-3.5 mr-1" /> Mi Contraseña
          </Button>
          <Button onClick={() => { resetCreateForm(); setShowCreate(true); }}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-3" data-testid="create-user-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo Usuario
          </Button>
        </div>
      </div>

      {/* Orphans banner */}
      {orphansCount > 0 && (
        <Card className="border-amber-300 bg-amber-50 rounded-sm" data-testid="orphans-banner">
          <CardContent className="p-3 flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  Hay {orphansCount} usuario{orphansCount > 1 ? "s" : ""} sin empleado/persona vinculada
                </p>
                <p className="text-xs text-amber-800">
                  La regla del sistema exige que cada usuario esté vinculado a una ficha. Vincula manualmente o marca como excepción.
                </p>
              </div>
            </div>
            <Button onClick={() => navigate("/admin/usuarios/migracion")}
              className="bg-amber-700 hover:bg-amber-800 text-white rounded-sm text-xs uppercase font-semibold h-8 px-3 whitespace-nowrap"
              data-testid="open-migration-btn">
              Abrir migración <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters Bar */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-3">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1 min-w-0 lg:max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, email o rol..."
                className="pl-8 h-8 text-xs rounded-sm border-slate-200" data-testid="users-search-input" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="h-8 w-auto min-w-[120px] text-xs rounded-sm border-slate-200" data-testid="filter-role"><SelectValue placeholder="Rol" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los roles</SelectItem>
                  {allRoles.map(r => <SelectItem key={r.slug} value={r.slug}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-8 w-auto min-w-[110px] text-xs rounded-sm border-slate-200" data-testid="filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="desactivado">Desactivado</SelectItem>
                  <SelectItem value="bloqueado">Bloqueado</SelectItem>
                  <SelectItem value="archivado">Archivado</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterLinkUser} onValueChange={setFilterLinkUser}>
                <SelectTrigger className="h-8 w-auto min-w-[150px] text-xs rounded-sm border-slate-200" data-testid="filter-link-user"><SelectValue placeholder="Vinculación" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Vinculación: Todos</SelectItem>
                  <SelectItem value="with_employee">Con persona</SelectItem>
                  <SelectItem value="no_employee">Sin persona</SelectItem>
                  <SelectItem value="exception">Excepción</SelectItem>
                  <SelectItem value="emp_archived">Ficha archivada</SelectItem>
                  <SelectItem value="emp_on_leave">Ficha de baja</SelectItem>
                  <SelectItem value="inconsistencies">Inconsistencias</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs text-red-600 hover:text-red-700 px-2" data-testid="clear-filters-btn">
                  <X className="w-3 h-3 mr-1" /> Limpiar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold cursor-pointer select-none" onClick={() => toggleSort("name")}>
                    <span className="flex items-center gap-1">Usuario <SortIcon field="name" /></span>
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Email</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Rol</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Persona vinculada</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold hidden md:table-cell">Centro · Dpto</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Estado usuario</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold hidden lg:table-cell">Última actividad</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Cargando...</TableCell></TableRow>
                ) : users.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400" data-testid="no-results-msg">
                    {hasActiveFilters ? "No se han encontrado usuarios con los filtros aplicados" : "No hay usuarios"}
                  </TableCell></TableRow>
                ) : (
                  users.map(u => {
                    const isMe = u.id === currentUser?.id;
                    const status = u.status || (u.active === false ? "desactivado" : "activo");
                    const sc = STATUS_CONFIG[status] || STATUS_CONFIG.activo;
                    const StatusIcon = sc.icon;
                    return (
                      <TableRow key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-xs font-semibold">{u.name?.charAt(0) || "U"}</span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{u.name} {isMe && <span className="text-[10px] text-slate-400">(tú)</span>}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                        <TableCell>
                          <Badge className={`rounded-sm text-[10px] border ${getRoleBadge(u.role)}`}>{getRoleLabel(u.role)}</Badge>
                          <span className="text-[9px] text-slate-300 ml-1">Nv.{u.role_level || 0}</span>
                        </TableCell>
                        <TableCell data-testid={`user-link-${u.id}`}>
                          {u.link_status === "linked" && (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs text-slate-800 font-medium truncate max-w-[160px]">{u.employee_name}</span>
                              <div className="flex flex-wrap items-center gap-1">
                                <Badge className={`rounded-sm text-[9px] border w-fit ${PERSON_TYPE_BADGE[u.person_type] || "bg-slate-50 text-slate-600"}`}>
                                  {PERSON_TYPE_LABEL[u.person_type] || u.person_type || "—"}
                                </Badge>
                                {u.email_sync_status === "synced" && (
                                  <Badge className="bg-green-50 text-green-700 text-[9px] rounded-sm border border-green-200" data-testid={`email-sync-badge-${u.id}`} title="Email de acceso coincide con el corporativo del empleado">
                                    <CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5" /> Sincronizado
                                  </Badge>
                                )}
                                {u.email_sync_status === "mismatch" && (
                                  <Badge className="bg-amber-50 text-amber-700 text-[9px] rounded-sm border border-amber-200" data-testid={`email-sync-badge-${u.id}`} title={`Empleado: ${u.employee_email || "—"}`}>
                                    <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" /> Email distinto
                                  </Badge>
                                )}
                                {u.email_sync_status === "no_employee_email" && (
                                  <Badge className="bg-slate-50 text-slate-600 text-[9px] rounded-sm border border-slate-200" data-testid={`email-sync-badge-${u.id}`} title="El empleado no tiene email corporativo">
                                    Sin email corp.
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                          {u.link_status === "no_employee" && (
                            <Badge className="bg-red-100 text-red-600 text-[9px] rounded-sm border border-red-200" data-testid={`email-sync-badge-${u.id}`}>Sin empleado</Badge>
                          )}
                          {u.link_status === "emp_archived" && (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs text-slate-500 truncate max-w-[160px]">{u.employee_name}</span>
                              <Badge className="bg-amber-100 text-amber-700 text-[9px] rounded-sm border border-amber-200">Ficha archivada</Badge>
                            </div>
                          )}
                          {u.link_status === "emp_on_leave" && (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs text-slate-500 truncate max-w-[160px]">{u.employee_name}</span>
                              <Badge className="bg-orange-100 text-orange-700 text-[9px] rounded-sm border border-orange-200">Persona de baja</Badge>
                            </div>
                          )}
                          {u.link_status === "exception" && (
                            <Badge className="bg-indigo-100 text-indigo-700 text-[9px] rounded-sm border border-indigo-200" title={u.exception_notes}>
                              <ShieldCheck className="w-2.5 h-2.5 inline mr-1" />
                              {u.exception_reason === "super_admin_initial" ? "Super Admin inicial" : "Técnico de sistema"}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 hidden md:table-cell">
                          {u.center_label || u.department_label ? (
                            <div className="flex flex-col gap-0.5">
                              {u.center_label && <span className="truncate max-w-[140px]">{u.center_label}</span>}
                              {u.department_label && <span className="text-slate-400 truncate max-w-[140px]">{u.department_label}</span>}
                            </div>
                          ) : <span className="text-slate-300">—</span>}
                        </TableCell>
                        <TableCell>
                          <Badge className={`rounded-sm text-[10px] border ${sc.badge}`}>
                            <StatusIcon className="w-2.5 h-2.5 inline mr-0.5" /> {sc.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 font-mono hidden lg:table-cell">
                          {u.last_login ? u.last_login.slice(0, 16).replace("T", " ") : <span className="text-slate-300">Nunca</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="text-xs h-7" data-testid={`edit-user-${u.id}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            {!isMe && status === "activo" && (
                              <Button variant="ghost" size="sm" onClick={() => openStatus(u, "desactivado")} className="text-xs h-7 text-slate-600" title="Desactivar" data-testid={`deactivate-user-${u.id}`}>
                                <UserMinus className="w-3 h-3" />
                              </Button>
                            )}
                            {!isMe && status !== "activo" && (
                              <Button variant="ghost" size="sm" onClick={() => openStatus(u, "activo")} className="text-xs h-7 text-green-600" title="Reactivar" data-testid={`reactivate-user-${u.id}`}>
                                <CheckCircle2 className="w-3 h-3" />
                              </Button>
                            )}
                            {!isMe && status !== "bloqueado" && (
                              <Button variant="ghost" size="sm" onClick={() => openStatus(u, "bloqueado")} className="text-xs h-7 text-red-600" title="Bloquear" data-testid={`block-user-${u.id}`}>
                                <Lock className="w-3 h-3" />
                              </Button>
                            )}
                            {!isMe && status === "bloqueado" && (
                              <Button variant="ghost" size="sm" onClick={() => openStatus(u, "activo")} className="text-xs h-7 text-green-600" title="Desbloquear" data-testid={`unblock-user-${u.id}`}>
                                <Unlock className="w-3 h-3" />
                              </Button>
                            )}
                            {isSuperAdmin && !isMe && (
                              <Button variant="ghost" size="sm" onClick={() => { setDeleteConfirm(u); setDeleteText(""); }} className="text-xs h-7 text-red-700 hover:text-red-800" title="Eliminar definitivo" data-testid={`delete-user-${u.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-400">Pág. {page} de {totalPages}</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-xs h-7 rounded-sm">Anterior</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-xs h-7 rounded-sm">Siguiente</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ───────── Create User Dialog ───────── */}
      <Dialog open={showCreate} onOpenChange={(o) => { if (!o) resetCreateForm(); setShowCreate(o); }}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase">Nuevo Usuario</DialogTitle>
            <DialogDescription className="text-xs text-slate-500 mt-1">Todo usuario debe estar vinculado a una persona (empleado, externo, proveedor o técnico de sistema).</DialogDescription>
          </DialogHeader>

          {/* Mode selector */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 my-3">
            <button onClick={() => setForm({ ...form, mode: "existing" })}
              className={`p-3 border rounded-sm text-left transition-all ${form.mode === "existing" ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 hover:border-slate-400"}`}
              data-testid="create-mode-existing">
              <Users className="w-4 h-4 mb-1 text-slate-700" />
              <p className="text-xs font-semibold">Empleado/persona existente</p>
              <p className="text-[10px] text-slate-500 mt-0.5">Vincular a una ficha sin usuario.</p>
            </button>
            <button onClick={() => setForm({ ...form, mode: "new" })}
              className={`p-3 border rounded-sm text-left transition-all ${form.mode === "new" ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 hover:border-slate-400"}`}
              data-testid="create-mode-new">
              <Plus className="w-4 h-4 mb-1 text-slate-700" />
              <p className="text-xs font-semibold">Crear ficha rápida</p>
              <p className="text-[10px] text-slate-500 mt-0.5">Nueva persona y usuario a la vez.</p>
            </button>
            {isSuperAdmin && (
              <button onClick={() => setForm({ ...form, mode: "exception" })}
                className={`p-3 border rounded-sm text-left transition-all ${form.mode === "exception" ? "border-amber-700 bg-amber-50 ring-1 ring-amber-700" : "border-amber-200 hover:border-amber-400"}`}
                data-testid="create-mode-exception">
                <ShieldAlert className="w-4 h-4 mb-1 text-amber-700" />
                <p className="text-xs font-semibold">Excepción del sistema</p>
                <p className="text-[10px] text-amber-700 mt-0.5">Solo Super Admin · técnico</p>
              </button>
            )}
          </div>

          {/* Common fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Email del usuario *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="new-user-email" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Contraseña *</Label>
              <Input type="password" value={form.password} maxLength={128} onChange={(e) => setForm({...form, password: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="new-user-password" />
              <PasswordStrengthMeter
                password={form.password}
                userData={{
                  name: `${form.ne_first_name || ""} ${form.ne_last_name || ""}`.trim(),
                  email: form.email,
                  phone: form.ne_phone,
                }}
                role={form.role}
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.filter(r => r.can_assign !== false).map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Mode: existing */}
          {form.mode === "existing" && (
            <div className="space-y-2 mt-3 border-t border-slate-100 pt-3" data-testid="existing-employee-section">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Buscar empleado/persona</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input value={employeeQuery} onChange={(e) => setEmployeeQuery(e.target.value)}
                  placeholder="Nombre, email, razón social..." className="pl-8 h-9 text-xs rounded-sm border-slate-200" data-testid="employee-search-input" />
              </div>
              <div className="max-h-52 overflow-y-auto border border-slate-200 rounded-sm">
                {availableEmployees.length === 0 ? (
                  <div className="p-3 text-center text-xs text-slate-400">Sin resultados. Todos los empleados ya tienen usuario o están archivados.</div>
                ) : availableEmployees.map(emp => (
                  <button key={emp.id} type="button" onClick={() => setForm({...form, employee_id: emp.id})}
                    className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50 ${form.employee_id === emp.id ? "bg-slate-100" : ""}`}
                    data-testid={`employee-option-${emp.id}`}>
                    <div className="flex justify-between items-center gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-800 truncate">{emp.name || emp.company_name}</p>
                        <p className="text-[10px] text-slate-500 truncate">
                          {emp.email || "—"} · {emp.center_name || emp.center || "—"} · {emp.department_name || emp.department || "—"}
                        </p>
                      </div>
                      <Badge className={`rounded-sm text-[9px] border ${PERSON_TYPE_BADGE[emp.person_type || "empleado"]}`}>
                        {PERSON_TYPE_LABEL[emp.person_type || "empleado"]}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Mode: new */}
          {form.mode === "new" && (
            <div className="space-y-3 mt-3 border-t border-slate-100 pt-3" data-testid="new-employee-section">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Tipo de persona</Label>
                <Select value={form.ne_person_type} onValueChange={(v) => setForm({...form, ne_person_type: v})}>
                  <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid="new-emp-person-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="empleado">Empleado interno</SelectItem>
                    <SelectItem value="externo">Externo / Consultor</SelectItem>
                    <SelectItem value="proveedor">Proveedor</SelectItem>
                    <SelectItem value="tecnico_sistema">Técnico de sistema</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Nombre {form.ne_person_type === "proveedor" ? "" : "*"}</Label>
                  <Input value={form.ne_first_name} onChange={(e) => setForm({...form, ne_first_name: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="new-emp-first-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Apellidos</Label>
                  <Input value={form.ne_last_name} onChange={(e) => setForm({...form, ne_last_name: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="new-emp-last-name" />
                </div>
                {(form.ne_person_type === "externo" || form.ne_person_type === "proveedor") && (
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Razón social / Empresa</Label>
                    <Input value={form.ne_company_name} onChange={(e) => setForm({...form, ne_company_name: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="new-emp-company-name" />
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Email ficha</Label>
                  <Input type="email" value={form.ne_email} onChange={(e) => setForm({...form, ne_email: e.target.value})} placeholder={form.email || "Mismo que el usuario"} className="rounded-sm h-9 border-slate-200" data-testid="new-emp-email" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Teléfono</Label>
                  <Input value={form.ne_phone} onChange={(e) => setForm({...form, ne_phone: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="new-emp-phone" />
                </div>
                {form.ne_person_type === "empleado" && (
                  <>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Centro</Label>
                      <Select value={form.ne_center_id || "_"} onValueChange={(v) => setForm({...form, ne_center_id: v === "_" ? "" : v})}>
                        <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid="new-emp-center"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_">—</SelectItem>
                          {centers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Departamento</Label>
                      <Select value={form.ne_department_id || "_"} onValueChange={(v) => setForm({...form, ne_department_id: v === "_" ? "" : v})}>
                        <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid="new-emp-department"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_">—</SelectItem>
                          {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Puesto</Label>
                      <Select value={form.ne_position_id || "_"} onValueChange={(v) => setForm({...form, ne_position_id: v === "_" ? "" : v})}>
                        <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid="new-emp-position"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_">—</SelectItem>
                          {positions.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Observaciones</Label>
                  <Textarea value={form.ne_notes} onChange={(e) => setForm({...form, ne_notes: e.target.value})} className="rounded-sm border-slate-200 text-xs" rows={2} data-testid="new-emp-notes" />
                </div>
              </div>
            </div>
          )}

          {/* Mode: exception */}
          {form.mode === "exception" && isSuperAdmin && (
            <div className="space-y-3 mt-3 border-t border-amber-100 pt-3 bg-amber-50/30 -mx-6 px-6 -mb-2 pb-2" data-testid="exception-section">
              <p className="text-xs text-amber-800 flex items-start gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                Solo para casos imprescindibles: Super Administrador inicial o cuenta técnica de servicio. Quedará auditado.
              </p>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-700">Motivo</Label>
                <Select value={form.exception_reason} onValueChange={(v) => setForm({...form, exception_reason: v})}>
                  <SelectTrigger className="rounded-sm h-9 border-amber-200" data-testid="exception-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tecnico_sistema">Cuenta técnica de sistema</SelectItem>
                    <SelectItem value="super_admin_initial">Super Administrador inicial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-700">Justificación (mín. 5 caracteres) *</Label>
                <Textarea value={form.exception_notes} onChange={(e) => setForm({...form, exception_notes: e.target.value})} className="rounded-sm border-amber-200 text-xs" rows={2} data-testid="exception-notes" />
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={handleCreate} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold" data-testid="create-user-submit">Crear Usuario</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ───────── Edit User Dialog ───────── */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="heading-industrial text-lg uppercase">Editar Usuario</DialogTitle></DialogHeader>
          <div className="space-y-4 py-3">
            <p className="text-[10px] text-slate-500">Nombre se hereda automáticamente desde la ficha vinculada (si la hay). Para cambiarlo, edita la ficha del empleado.</p>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                className="rounded-sm h-9 border-slate-200 read-only:bg-slate-50 read-only:text-slate-500 read-only:cursor-not-allowed"
                readOnly={!!editUser?.employee_id}
                disabled={!!editUser?.employee_id}
                data-testid="edit-user-email"
              />
              {editUser?.employee_id && (
                <div className="space-y-1.5">
                  <p className="text-[10px] text-slate-500 italic" data-testid="edit-user-email-hint">
                    El email se hereda automáticamente desde la ficha del empleado. Para cambiarlo, edita el empleado vinculado.
                  </p>
                  {(() => {
                    const empEmail = (editUser?.employee_email || "").trim();
                    const userEmail = (editForm.email || "").trim();
                    const inSync = empEmail && empEmail.toLowerCase() === userEmail.toLowerCase();
                    const noEmpEmail = !empEmail;
                    return (
                      <>
                        {noEmpEmail ? (
                          <p className="text-[11px] text-amber-700" data-testid="edit-user-no-corp-email">
                            El empleado no tiene email corporativo.
                          </p>
                        ) : (
                          <p className="text-[11px] text-slate-600" data-testid="edit-user-corp-email">
                            Email corporativo del empleado: <span className="font-mono text-slate-800">{empEmail}</span>
                          </p>
                        )}
                        {!noEmpEmail && !inSync && (
                          <p className="text-[10px] text-amber-700" data-testid="edit-user-mismatch-notice">
                            El email de acceso no coincide con el email corporativo.
                          </p>
                        )}
                        {inSync ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled
                            className="rounded-sm text-xs uppercase h-7 text-green-700 border-green-200 bg-green-50 cursor-default"
                            data-testid="edit-user-already-synced"
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Ya sincronizado
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={noEmpEmail}
                            onClick={() => syncEmailWithEmployee(editUser.id)}
                            className="rounded-sm text-xs uppercase h-7"
                            data-testid="edit-user-sync-email-btn"
                          >
                            Usar email corporativo
                          </Button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nueva contraseña</Label>
              <Input type="password" value={editForm.password} maxLength={128} onChange={(e) => setEditForm({...editForm, password: e.target.value})} placeholder="Dejar vacío para no cambiar" className="rounded-sm h-9 border-slate-200" data-testid="edit-user-password" />
              {editForm.password && (
                <>
                  <PasswordStrengthMeter
                    password={editForm.password}
                    userData={{ name: editUser?.name, email: editForm.email }}
                    role={editForm.role}
                  />
                  <p className="text-[10px] text-amber-700 mt-1">
                    El usuario tendrá que cambiarla en su próximo inicio de sesión.
                  </p>
                </>
              )}
              <p className="text-[10px] text-slate-400 mt-1">
                Alternativa segura: usa el botón «Enviar enlace de activación» para que el usuario defina su propia contraseña.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Rol</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({...editForm, role: v})}>
                <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.filter(r => r.can_assign !== false).map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-[10px] text-slate-400">Creado: {formatDateES(editUser?.created_at) || "—"}</p>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => handleSendActivationLink(editUser)}
                className="rounded-sm text-[10px] uppercase tracking-wider h-8 border-blue-200 text-blue-700 hover:bg-blue-50"
                data-testid="send-activation-link-btn"
              >
                <Key className="w-3 h-3 mr-1" /> Enviar enlace de activación
              </Button>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => handleGenerateTempPassword(editUser)}
                className="rounded-sm text-[10px] uppercase tracking-wider h-8 border-amber-200 text-amber-700 hover:bg-amber-50"
                data-testid="generate-temp-password-btn"
              >
                <Lock className="w-3 h-3 mr-1" /> Contraseña temporal
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={handleEdit} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold" data-testid="edit-user-submit">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ───────── Status Change Dialog ───────── */}
      <Dialog open={!!showStatusDialog} onOpenChange={() => setShowStatusDialog(null)}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="status-change-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase">Cambiar estado</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-slate-600">
              Usuario: <span className="font-semibold">{showStatusDialog?.name}</span> ({showStatusDialog?.email})
            </p>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Nuevo estado</Label>
              <Select value={statusForm.status} onValueChange={(v) => setStatusForm({...statusForm, status: v})}>
                <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">Activo (acceso normal)</SelectItem>
                  <SelectItem value="desactivado">Desactivado (sin acceso)</SelectItem>
                  <SelectItem value="bloqueado">Bloqueado (forzado)</SelectItem>
                  <SelectItem value="archivado">Archivado (histórico)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Motivo (opcional)</Label>
              <Textarea value={statusForm.reason} onChange={(e) => setStatusForm({...statusForm, reason: e.target.value})} rows={2} className="rounded-sm border-slate-200 text-xs" data-testid="status-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(null)} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={handleChangeStatus} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold" data-testid="status-submit">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ───────── Delete Confirmation Dialog (only super_admin) ───────── */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => { setDeleteConfirm(null); setDeleteText(""); }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase text-red-700">Eliminar Usuario Definitivamente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-red-50 border border-red-200 rounded-sm p-3">
              <p className="text-xs text-red-700 font-semibold">Acción irreversible</p>
              <p className="text-xs text-red-600 mt-1">
                Se eliminará permanentemente al usuario <span className="font-mono">{deleteConfirm?.email}</span>.
                La ficha de empleado/persona vinculada NO se borrará. Si solo quieres revocar acceso, usa "Desactivar" o "Archivar".
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-700">Escribe <span className="font-mono bg-slate-100 px-1">ELIMINAR</span> para confirmar</Label>
              <Input value={deleteText} onChange={(e) => setDeleteText(e.target.value)} className="rounded-sm h-9 border-red-200 font-mono" data-testid="delete-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteConfirm(null); setDeleteText(""); }} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={handleDelete} disabled={deleteText !== "ELIMINAR"}
              className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 rounded-sm text-xs uppercase font-semibold" data-testid="delete-confirm-submit">
              Eliminar definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ───────── Change Password Dialog ───────── */}
      <Dialog open={showPwChange} onOpenChange={setShowPwChange}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="change-password-dialog">
          <DialogHeader><DialogTitle className="heading-industrial text-lg uppercase">Cambiar Mi Contraseña</DialogTitle></DialogHeader>
          <div className="space-y-4 py-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Contraseña actual *</Label>
              <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({...pwForm, current_password: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="pw-current" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nueva contraseña *</Label>
              <Input type="password" value={pwForm.new_password} maxLength={128} onChange={(e) => setPwForm({...pwForm, new_password: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="pw-new" />
              <PasswordStrengthMeter
                password={pwForm.new_password}
                userData={{ name: currentUser?.name, email: currentUser?.email }}
                role={currentUser?.role}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Confirmar nueva contraseña *</Label>
              <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({...pwForm, confirm: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="pw-confirm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPwChange(false)} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={handlePasswordChange} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold" data-testid="pw-submit">Cambiar Contraseña</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
